import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';

/**
 * Shared utilities for handling demo mode across managers
 */

/**
 * Check if we're in demo mode with a specific tutorial flow
 */
export function isDemoModeWithTutorial(tutorialFlow: string): boolean {
  if (!isDemoMode()) {
    return false;
  }
  return localStorage.getItem('demo_tutorial_flow') === tutorialFlow;
}

/**
 * Get demo organisation if in demo mode with organisational tutorial
 */
export function getDemoOrganisationIfActive() {
  if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
    return DemoDataManager.getDemoOrganisation();
  }
  return null;
}

/**
 * Execute a function with demo mode fallback
 * @param demoFn - Function to execute in demo mode
 * @param firestoreFn - Function to execute in normal mode
 * @param fallbackDemoFn - Optional fallback if firestoreFn fails in demo mode
 */
export async function withDemoModeFallback<T>(
  demoFn: () => T | Promise<T>,
  firestoreFn: () => T | Promise<T>,
  fallbackDemoFn?: () => T | Promise<T>
): Promise<T> {
  if (isDemoMode()) {
    try {
      return await demoFn();
    } catch (error) {
      console.warn('Demo mode function failed, trying fallback:', error);
      if (fallbackDemoFn) {
        return await fallbackDemoFn();
      }
      throw error;
    }
  }

  try {
    return await firestoreFn();
  } catch (error) {
    // If Firestore fails and we're in demo mode, try demo fallback
    if (isDemoMode() && fallbackDemoFn) {
      console.warn('Firestore function failed in demo mode, using demo fallback:', error);
      return await fallbackDemoFn();
    }
    throw error;
  }
}

/**
 * Execute a function only if not in demo mode
 * @param fn - Function to execute
 */
export async function skipInDemoMode<T>(
  fn: () => T | Promise<T>
): Promise<T | void> {
  if (isDemoMode()) {
    return;
  }
  return await fn();
}
